import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import type { PreviewResult } from '../../api/audit-config-queries';
import WriteGuard from '../WriteGuard';

interface PreviewDialogProps {
  open: boolean;
  preview: PreviewResult | null;
  /** Preview request in flight. */
  previewLoading?: boolean;
  /** Preview request failed (message includes HTTP status + server detail). */
  error?: string | null;
  /** Save (PUT) in flight — Apply shows a spinner and the dialog cannot be dismissed. */
  applying: boolean;
  /** Save failed or stored a different value; the dialog stays open showing this. */
  applyError?: string | null;
  /** Set when the user lacks the Audit Admin role: Apply is disabled with this tooltip. */
  writeDisabledReason?: string | null;
  onApply: () => void;
  onCancel: () => void;
}

/**
 * Shared preview + confirm modal for audit config changes.
 *
 * The dialog IS the confirmation step: Apply saves directly (no native confirm).
 * It closes only when the parent reports a successful save; on failure it stays
 * open with the server's status and message.
 */
export default function PreviewDialog({
  open,
  preview,
  previewLoading = false,
  error,
  applying,
  applyError,
  writeDisabledReason = null,
  onApply,
  onCancel,
}: PreviewDialogProps) {
  function buildTitle(): string {
    if (error) return 'Preview unavailable';
    if (!preview) return 'Computing preview…';
    const { totalAffected, byClient } = preview.summary;
    const clientCount = Object.keys(byClient).length;
    return `This change would affect ${totalAffected.toString()} device${totalAffected !== 1 ? 's' : ''} across ${clientCount.toString()} client${clientCount !== 1 ? 's' : ''}`;
  }

  const staleGaps = preview?.expectedGapMatches?.filter((m) => m.stale) ?? [];
  const canApply = !!preview && !error && !previewLoading && !applying && !writeDisabledReason;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={applying ? undefined : onCancel}
      aria-labelledby="preview-dialog-title"
    >
      <DialogTitle id="preview-dialog-title">{buildTitle()}</DialogTitle>

      <DialogContent dividers>
        {applyError && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {applyError}
          </Alert>
        )}

        {staleGaps.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {staleGaps.length} expected gap{staleGaps.length !== 1 ? 's' : ''} match
            {staleGaps.length === 1 ? 'es' : ''} no devices in the latest audit and may be stale:{' '}
            {staleGaps.map((g) => (
              <Chip
                key={`${g.clientName}|${g.type}`}
                label={`${g.clientName} / ${g.type}`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ mr: 0.5, mt: 0.5 }}
              />
            ))}
          </Alert>
        )}

        {error ? (
          <Typography color="error" sx={{ py: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        ) : preview === null ? (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Analyzing affected devices across all clients&hellip;
            </Typography>
          </Box>
        ) : preview.affectedDevices.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No devices affected by this change.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Current</TableCell>
                <TableCell>Proposed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.affectedDevices.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
                    {row.deviceName}
                  </TableCell>
                  <TableCell>{row.clientName}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.field}</TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.8rem',
                      color: 'error.main',
                    }}
                  >
                    {row.oldValue}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.8rem',
                      color: 'success.main',
                    }}
                  >
                    {row.newValue}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="inherit" disabled={applying}>
          Cancel
        </Button>
        <WriteGuard reason={writeDisabledReason}>
          <Button
            variant="contained"
            onClick={onApply}
            disabled={!canApply}
            startIcon={applying ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {applying ? 'Applying…' : 'Apply Changes'}
          </Button>
        </WriteGuard>
      </DialogActions>
    </Dialog>
  );
}
